import * as yup from "yup";

/**
 * Cost type enum values (must match backend ClaimCostType enum)
 */
export const COST_TYPES = {
  LEAK_DETECTION: "leak_detection",
  REPAIR: "repair",
  DRYING: "drying",
  RESTORATION: "restoration",
  ELECTRICITY: "electricity",
  RENT_LOSS: "rent_loss",
  RENT_REDUCTION: "rent_reduction",
  ACCOMMODATION: "accommodation",
  OTHER: "other",
};

/**
 * German labels for cost types
 */
export const COST_TYPE_LABELS = {
  [COST_TYPES.LEAK_DETECTION]: "Leckortung",
  [COST_TYPES.REPAIR]: "Reparaturkosten (Schadenursache)",
  [COST_TYPES.DRYING]: "Trocknung",
  [COST_TYPES.RESTORATION]: "Wiederherstellungskosten",
  [COST_TYPES.ELECTRICITY]: "Stromkosten",
  [COST_TYPES.RENT_LOSS]: "Mietausfall",
  [COST_TYPES.RENT_REDUCTION]: "Mietminderung",
  [COST_TYPES.ACCOMMODATION]: "Ersatzunterkunft/Hotelkosten",
  [COST_TYPES.OTHER]: "Sonstige",
};

/**
 * Cost type options for select dropdown
 */
export const COST_TYPE_OPTIONS = Object.entries(COST_TYPE_LABELS).map(
  ([key, label]) => ({
    key,
    label,
  })
);

/**
 * Amount field labels per cost type
 */
export const AMOUNT_LABELS = {
  [COST_TYPES.ELECTRICITY]: "Stromkosten",
  [COST_TYPES.RENT_LOSS]: "Höhe des Mietausfalls",
  [COST_TYPES.RENT_REDUCTION]: "Höhe der Mietminderung",
  default: "Rechnungsbetrag",
};

export const getAmountLabel = (costType) => {
  return AMOUNT_LABELS[costType] || AMOUNT_LABELS.default;
};

export const getAttachmentLabel = (costType, fieldName) => {
  return (
    TYPE_FIELD_CONFIG[costType]?.uploadFields.find(
      (field) => field.name === fieldName
    )?.label || "Anhang"
  );
};

/**
 * Get all attachment field names for a given cost type
 */
export const getAttachmentFields = (costType) => {
  const config = TYPE_FIELD_CONFIG[costType];
  return config?.uploadFields?.map((field) => field.name) || [];
};

/**
 * Check if a field name is an attachment field for a given cost type
 */
export const isAttachmentField = (costType, fieldName) => {
  const attachmentFields = getAttachmentFields(costType);
  return attachmentFields.includes(fieldName);
};

/**
 * Base schema for common fields
 */
const baseSchema = {
  cost_type: yup.string().required("Kostenart ist erforderlich"),
  amount: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .min(0, "Betrag darf nicht negativ sein"),
  company_name: yup.string().nullable(),
  invoice_number: yup.string().nullable(),
  invoice_date: yup.date().nullable(),
};

/**
 * Type-specific field definitions
 * Each type defines which fields are shown and any additional fields in the data column
 */
export const TYPE_FIELD_CONFIG = {
  [COST_TYPES.LEAK_DETECTION]: {
    showFields: ["company_name", "invoice_number", "invoice_date", "amount"],
    dataFields: [],
    uploadFields: [{ name: "invoice_attachment", label: "Rechnung" }],
  },
  [COST_TYPES.REPAIR]: {
    showFields: ["company_name", "invoice_number", "invoice_date", "amount"],
    dataFields: [],
    uploadFields: [{ name: "invoice_attachment", label: "Rechnung" }],
  },
  [COST_TYPES.DRYING]: {
    showFields: ["company_name", "invoice_number", "invoice_date", "amount"],
    dataFields: [],
    uploadFields: [{ name: "invoice_attachment", label: "Rechnung" }],
  },
  [COST_TYPES.RESTORATION]: {
    showFields: ["company_name", "invoice_number", "invoice_date", "amount"],
    dataFields: [
      {
        name: "completion_date",
        label: "Fertigstellungsdatum",
        type: "date",
      },
    ],
    uploadFields: [{ name: "invoice_attachment", label: "Rechnung" }],
  },
  [COST_TYPES.ELECTRICITY]: {
    showFields: ["amount"],
    dataFields: [
      {
        name: "drying_period",
        label: "Trocknungszeitraum",
        type: "text",
      },
      {
        name: "kwh_consumed",
        label: "Verbrauchte kWh",
        type: "number",
        unit: "kWh",
      },
      {
        name: "price_per_kwh",
        label: "Preis pro kWh",
        type: "number",
        unit: "€",
      },
    ],
    uploadFields: [{ name: "invoice_attachment", label: "Rechnung" }],
  },
  [COST_TYPES.RENT_REDUCTION]: {
    showFields: ["amount"], // höhe mietminderung
    dataFields: [
      {
        name: "reduction_period",
        label: "Zeitraum für die Mietminderung",
        type: "text",
      },
    ],
    uploadFields: [
      { name: "invoice_attachment", label: "Rechnung/Bestätigung" },
      {
        name: "tenant_demand_letter_attachment",
        label: "Forderungsschreiben des Mieters",
      },
      {
        name: "rent_amount_confirmation_attachment",
        label: "Mietvertrag/Bestätigung aktueller Mietpreis",
      },
    ],
  },
  [COST_TYPES.RENT_LOSS]: {
    showFields: ["amount"],
    dataFields: [
      {
        name: "loss_reason",
        label: "Grund des Mietausfalls",
        type: "text",
      },
      {
        name: "loss_duration",
        label: "Dauer",
        type: "text",
      },
    ],
    uploadFields: [
      {
        name: "rent_amount_confirmation_attachment",
        label: "Mietvertrag / Bestätigung aktueller Mietpreis",
      },
    ],
  },
  [COST_TYPES.ACCOMMODATION]: {
    showFields: ["company_name", "invoice_number", "invoice_date", "amount"],
    dataFields: [
      {
        name: "accommodation_period",
        label: "Zeitraum für die Unterbringung",
        type: "text",
      },
    ],
    uploadFields: [{ name: "invoice_attachment", label: "Rechnung" }],
  },
  [COST_TYPES.OTHER]: {
    showFields: ["company_name", "invoice_number", "invoice_date", "amount"],
    dataFields: [
      {
        order: -1, // render before common fields
        name: "cost_label",
        label: "Bezeichnung der Kosten",
        type: "text",
      },
    ],
    uploadFields: [{ name: "invoice_attachment", label: "Rechnung" }],
  },
};

/**
 * Get the validation schema for a specific cost type
 */
export const getSchemaForType = (costType) => {
  const config = TYPE_FIELD_CONFIG[costType];
  if (!config) {
    return yup.object().shape(baseSchema);
  }

  const dataShape = {};
  config.dataFields.forEach((field) => {
    if (field.type === "number") {
      dataShape[field.name] = yup
        .number()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        );
    } else if (field.type === "date") {
      dataShape[field.name] = yup.date().nullable();
    } else {
      dataShape[field.name] = yup.string().nullable();
    }
  });

  return yup.object().shape({
    ...baseSchema,
    data: yup.object().shape(dataShape),
  });
};

/**
 * Get the full validation schema (used for the form)
 */
export const costEntrySchema = yup.object().shape({
  cost_type: yup.string().required("Kostenart ist erforderlich"),
  amount: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .min(0, "Betrag darf nicht negativ sein"),
  company_name: yup.string().nullable(),
  invoice_number: yup.string().nullable(),
  invoice_date: yup.date().nullable(),
  data: yup.object().nullable(),
  is_assigned: yup.boolean().nullable(),
});
